import { devLog } from './utils/devLog';
// Helper to get the frontend URL dynamically
function getFrontendUrl() {
  // In production/deployed environments, use the actual origin
  if (typeof window !== 'undefined' && window.location.origin && 
      !window.location.origin.includes('localhost') && 
      !window.location.origin.includes('127.0.0.1')) {
    return window.location.origin;
  }
  // Development fallback (dev-full stack: frontend is served via Envoy on :8080)
  return 'http://localhost:8080';
}

const config = {
  // All values loaded from hub-fe-config.json at runtime
  bffUrl: null,
  agentUrl: null,
  publicBaseUrl: null,
  // ADK app name used for a chat session with no playbook selected. "hub" is
  // mia-flow-be's legacy app name; overridable (DEFAULT_APP_NAME env var, see
  // generate-config.sh) for a backend that doesn't serve that name — e.g.
  // adk-be-app (migration/vfs-adk-generation), which is playbook-scoped-name-only.
  defaultAppName: null,
  // vfs-service base URL (migration/vfs-adk-generation) — source of truth for a
  // project's source code once created without a git connection (see
  // src/utils/vfsClient.js). Overridable via VFS_SERVICE_URL, see generate-config.sh.
  vfsServiceUrl: null,
  // adk-be-app's ADK endpoint, PROXIED THROUGH mia-flow-bff (Phase 3:
  // mia-flow-bff's routes/adk-proxy.js) — used for a vfs-backed (git-free)
  // project's chat only, routed to adk-be-app's code-generation-agent
  // instead of mia-flow-be (which has no vfs-service tools, git/GitLab-only).
  // Was a direct-to-adk-be-app URL (Envoy's `/api/adk/` route, bypassing
  // mia-flow-bff); changed so the BFF can inject the caller's real Keycloak
  // bearer token onto every request adk-be-app receives (its
  // `bearer_token_var` otherwise has no reliable per-user identity — needed
  // by src/agent/mcp_tools.py's per-user vendor MCP token flow). Every
  // consumer of this value (VfsChatPanel's vendored ChatWidget `apiBaseUrl`
  // prop included) gets the new routing for free since they all just treat
  // this as an opaque base URL. See src/services/AdkSessionsService.js,
  // src/services/PlaybooksService.js, src/components/VfsChatPanel.jsx and
  // generate-config.sh.
  vfsAgentUrl: null,
  // Sandbox-first experience (docs/SANDBOX_FIRST.md): Home is the person's sandbox and chat is a feature they turn on.
  // Off keeps today's chat-first Flow. From SANDBOX_FIRST, see generate-config.sh.
  sandboxFirst: false,
  // The organization provides the chat models (AI Foundry / adk-be-app keys); false = bring your own key.
  chatOrgModels: true,
  // Sandbox-first only: chat starts on for someone who never chose (only with chatOrgModels).
  chatDefaultOn: true,
  apiKeyBff: null,
  apiKeyBe: null,
  consoleBaseUrl: null, // Mia Platform Console base URL
  // Console Next (Console v16) API base URL, e.g. http://localhost:8790. Empty = the Publish dialog keeps
  // "Mia-Platform Console" as coming soon. From CONSOLE_NEXT_URL, see generate-config.sh.
  consoleNextUrl: '',
  // Where Console Next's web app is, to open the delivered System. Empty = same as consoleNextUrl
  // (one binary serves both in production). From CONSOLE_NEXT_WEB_URL.
  consoleNextWebUrl: '',
  // Who people ask to be let in to Flow (an email or a name), shown in the AI tools' troubleshooting. Empty hides it.
  adminContact: '',
  // The release shown in the sidebar footer (FLOW_VERSION); '' hides it.
  flowVersion: '',
  aiFoundryBaseUrl: null, // AI Foundry application base URL
  get frontendUrl() {
    return getFrontendUrl();
  }, // Frontend application URL - MUST match Okta redirect URI configuration
  
  // Mia Platform project configuration (default / Web App mode).
  // No serviceTemplate* here: the default template (Mia Flow React Template)
  // is resolved dynamically by the BFF at request time, not pinned per environment.
  defaultAppServiceRevisionId: 'main',

  // Per-template configs for Full Stack mode
  // Keys map to the template name selected in the UI
  fullStackTemplateConfigs: {
    'React + Node.js Application': {
      templateItemId: null,
      templateTenantId: null,
      templateVersion: null,
      templateId: null,
    },
    'React + .NET Core Application': {
      templateItemId: null,
      templateTenantId: null,
      templateVersion: null,
      templateId: null,
    },
    'React + Java Application': {
      templateItemId: null,
      templateTenantId: null,
      templateVersion: null,
      templateId: null,
    },
    'Only Backend Java': {
      templateItemId: null,
      templateTenantId: null,
      templateVersion: null,
      templateId: null,
    },
    'Only Backend .NET': {
      templateItemId: null,
      templateTenantId: null,
      templateVersion: null,
      templateId: null,
    },
    'Only Backend Node.js': {
      templateItemId: null,
      templateTenantId: null,
      templateVersion: null,
      templateId: null,
    },
    'Next.js Application': {
      templateItemId: null,
      templateTenantId: null,
      templateVersion: null,
      templateId: null,
    },
  },
  
  // Token management configuration
  tokenLimits: {
    maxTokens: 1000000,           // Maximum tokens before forcing conversation restart (32K for most models)
    compressionThreshold: 0.5,  // Compress conversation at 50% of maxTokens (16K tokens)
    warningThreshold: 0.8,      // Show warning at 80% of maxTokens (25.6K tokens)
  },
  
};

export default config;

let hubConfig = {};

/** A boolean from hub-fe-config.json, where generate-config.sh writes every value as a string; unset keeps `fallback`. */
export function flag(value, fallback) {
  if (value === true || value === false) { return value; }
  if (typeof value !== 'string' || value.trim() === '') { return fallback; }
  return value.trim().toLowerCase() === 'true';
}

export async function loadHubConfig() {
    try {
      // hub-fe-config.json lives in the FE's public dir, which Vite serves under
      // the app's base path (import.meta.env.BASE_URL, e.g. '/flow/'). Fetching the
      // origin-root '/hub-fe-config.json' would miss it: Envoy 301-redirects any
      // non-matching path to '/flow/', so the fetch would silently receive the HTML
      // index instead of JSON, leaving config.bffUrl null and breaking every API call.
      const baseUrl = import.meta.env.BASE_URL || '/';
      const configUrl = `${baseUrl.replace(/\/$/, '')}/hub-fe-config.json`;
      const res = await fetch(configUrl);
      if (!res.ok) {
        // eslint-disable-next-line no-console
        console.warn('Failed to load hub-fe-config.json, using default config');
        return;
      }
      hubConfig = await res.json();
      
      // Override config with runtime values from hub-fe-config.json
      config.bffUrl = hubConfig.bffUrl;
      config.agentUrl = hubConfig.agentUrl;
      config.publicBaseUrl = hubConfig.publicBaseUrl;
      config.defaultAppName = hubConfig.defaultAppName || config.defaultAppName;
      config.vfsServiceUrl = hubConfig.vfsServiceUrl || config.vfsServiceUrl;
      config.vfsAgentUrl = hubConfig.vfsAgentUrl || config.vfsAgentUrl;
      config.sandboxFirst = flag(hubConfig.sandboxFirst, config.sandboxFirst);
      config.chatOrgModels = flag(hubConfig.chatOrgModels, config.chatOrgModels);
      config.chatDefaultOn = flag(hubConfig.chatDefaultOn, config.chatDefaultOn);
      config.apiKeyBff = hubConfig.apiKeyBff;
      config.apiKeyBe = hubConfig.apiKeyBe;
      config.consoleBaseUrl = hubConfig.consoleBaseUrl || config.consoleBaseUrl;
      config.consoleNextUrl = (hubConfig.consoleNextUrl || config.consoleNextUrl || '').replace(/\/+$/, '');
      config.flowVersion = String(hubConfig.flowVersion || '').trim();
      config.consoleNextWebUrl = (hubConfig.consoleNextWebUrl || config.consoleNextWebUrl || '').replace(/\/+$/, '');
      config.adminContact = String(hubConfig.adminContact || '').trim();
      config.aiFoundryBaseUrl = hubConfig.aiFoundryBaseUrl || config.aiFoundryBaseUrl;

      config.defaultAppServiceRevisionId = hubConfig.defaultAppServiceRevisionId;

      // Per-template configs for Full Stack mode
      const templateKeyMap = {
        'React + Node.js Application':  'reactNode',
        'React + .NET Core Application': 'reactDotnet',
        'React + Java Application':     'reactJava',
        'Only Backend Java':            'backendJava',
        'Only Backend .NET':            'backendDotnet',
        'Only Backend Node.js':         'backendNode',
        'Next.js Application':          'nextjs',
      };
      Object.entries(templateKeyMap).forEach(([templateName, key]) => {
        config.fullStackTemplateConfigs[templateName] = {
          templateItemId:   hubConfig[`${key}TemplateItemId`]   || null,
          templateTenantId: hubConfig[`${key}TemplateTenantId`] || null,
          templateVersion:  hubConfig[`${key}TemplateVersion`]  || null,
          templateId:       hubConfig[`${key}TemplateId`]       || null,
        };
      });

      // eslint-disable-next-line no-console
      devLog('Loaded runtime config:', {bffUrl: config.bffUrl, agentUrl: config.agentUrl});
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Error loading hub-fe-config.json:', error);
    }
}
  
export function getHubConfig() {
    return hubConfig;
}

// Check if the configuration has been loaded
export function isConfigLoaded() {
  return !!(config.bffUrl && config.agentUrl);
}

